import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosInstance";

export default function RoleCreate() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState([]); // [{_id, name, permissions: [{_id, name, actions: [{_id, name}]}]}]
  const [user, setUser] = useState(null);
  const [formValues, setFormValues] = useState({
    roleName: "",
    description: "",
    permissions: {}, // { [groupId]: { [permissionId]: { [actionId]: boolean, selectAll: boolean } } }
    status: "Active"
  });
  const [initialRole, setInitialRole] = useState(null);

  // Get business ID and user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
  }, []);

  useEffect(() => {
    async function fetchAll() {
      try {
        const permRes = await axios.get('/admin/api/permission');
        const perms = permRes.data.permissions || permRes.data.data || [];
        const wrapped = [{ _id: null, name: 'Permissions', permissions: perms }];
        setPermissionGroups(wrapped);
      } catch (err) {
        console.error('Failed to load permissions:', err);
        // fallback to previous permissionGroup endpoint
        try {
          const groupRes = await axios.get('/admin/api/permissionGroup');
          setPermissionGroups(groupRes.data.groups || []);
        } catch (err2) {
          console.error('Failed to load permission groups:', err2);
          setPermissionGroups([]);
        }
      }
    }

    fetchAll();
  }, []);

  // Build initial permissions matrix (after permissionGroups loaded)
  useEffect(() => {
    if (permissionGroups.length) {
      let matrix = {};
      permissionGroups.forEach(group => {
        matrix[group._id] = {};
        (group.permissions || []).forEach(permission => {
          matrix[group._id][permission._id] = {};
          (permission.actions || []).forEach(action => {
            matrix[group._id][permission._id][action._id] = false;
          });
          matrix[group._id][permission._id].selectAll = false;
        });
      });
      setFormValues(f => ({
        ...f,
        permissions: matrix,
      }));
    }
  }, [permissionGroups]);

  // Fetch role for edit
  useEffect(() => {
    if (id) {
      setLoading(true);
      const url = `/admin/api/role/${id}`;
      axios.get(url)
        .then(res => setInitialRole(res.data.role))
        .finally(() => setLoading(false));
    }
  }, [id]);

  // Populate form for edit
  useEffect(() => {
    if (initialRole && permissionGroups.length) {
      let matrix = {};
      permissionGroups.forEach(group => {
        matrix[group._id] = {};
        (group.permissions || []).forEach(permission => {
          let foundGroup = Array.isArray(initialRole.permissionGroups)
            ? initialRole.permissionGroups.find(
                g => (g._id || g.group?._id || g.group) === group._id
              )
            : undefined;
          let foundPerm = foundGroup && Array.isArray(foundGroup.permissions)
            ? foundGroup.permissions.find(
                p => (p._id || p.permission?._id || p.permission) === permission._id
              )
            : undefined;
          matrix[group._id][permission._id] = {};
          (permission.actions || []).forEach(action => {
            let foundAction = foundPerm && Array.isArray(foundPerm.actions)
              ? foundPerm.actions.find(
                  a => (a._id || a.action?._id || a.action) === action._id
                )
              : undefined;
            matrix[group._id][permission._id][action._id] = !!(foundAction && foundAction.isSelected);
          });
          matrix[group._id][permission._id].selectAll =
            (permission.actions || []).length > 0 &&
            (permission.actions || []).every(action => matrix[group._id][permission._id][action._id]);
        });
      });
      setFormValues({
        roleName: initialRole.name || "",
        description: initialRole.description || "",
        permissions: matrix,
        status: initialRole.status || "Active"
      });
    }
  }, [initialRole, permissionGroups]);

  // Permission change handlers
  const handlePermissionChange = (groupId, permissionId, actionId, checked) => {
    const updated = { ...formValues.permissions };
    updated[groupId][permissionId][actionId] = checked;
    // Update selectAll for this permission row
    const allChecked = Object.keys(updated[groupId][permissionId])
      .filter(k => k !== "selectAll")
      .every(aid => updated[groupId][permissionId][aid]);
    updated[groupId][permissionId].selectAll = allChecked;
    setFormValues({ ...formValues, permissions: updated });
  };

  const handleRowSelectAll = (groupId, permissionId, checked) => {
    const updated = { ...formValues.permissions };
    Object.keys(updated[groupId][permissionId]).forEach(aid => {
      if (aid !== "selectAll") updated[groupId][permissionId][aid] = checked;
    });
    updated[groupId][permissionId].selectAll = checked;
    setFormValues({ ...formValues, permissions: updated });
  };

  const handleGlobalSelectAll = (checked) => {
    let updated = { ...formValues.permissions };
    permissionGroups.forEach(group => {
      (group.permissions || []).forEach(permission => {
        Object.keys(updated[group._id][permission._id]).forEach(aid => {
          if (aid !== "selectAll") updated[group._id][permission._id][aid] = checked;
        });
        updated[group._id][permission._id].selectAll = checked;
      });
    });
    setFormValues({ ...formValues, permissions: updated });
  };

  // Check if all rows are checked (global select all)
  const isGlobalAllChecked = permissionGroups.length > 0 && permissionGroups.every(
    group => (group.permissions || []).every(
      permission => formValues.permissions[group._id]?.[permission._id]?.selectAll
    )
  );

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get fresh data from localStorage at save time
    const storedUser = localStorage.getItem('user');
    let currentUserId = null;
    
    // Parse user data to get user ID
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        currentUserId = parsedUser._id || parsedUser.id;
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }

    // Build payload for API
    const permissionGroupsPayload = permissionGroups.map(group => ({
      group: group._id,
      permissions: (group.permissions || []).map(permission => {
        const allowedActions = Object.keys(formValues.permissions[group._id][permission._id] || {})
          .filter(aid => aid !== "selectAll" && formValues.permissions[group._id][permission._id][aid]);
        return {
          permission: permission._id,
          allowedActions
        };
      }).filter(p => p.allowedActions.length > 0)
    })).filter(pg => pg.permissions.length > 0);

    const payload = {
      name: formValues.roleName,
      description: formValues.description,
      permissionGroups: permissionGroupsPayload,
      status: formValues.status
    };

    // Add addedBy user ID if available
    if (currentUserId) {
      payload.addedBy = currentUserId;
    }

    try {
      if (id) {
        await axios.put(`/admin/api/role/${id}`, payload);
      } else {
        await axios.post("/admin/api/role", payload);
      }
      // Use absolute path for navigation
      navigate("/admin/userRole");
    } catch (err) {
      console.error('Error saving role:', err);
      alert("Failed to save role");
    }
  };

  if (id && loading) return <div>Loading...</div>;

  return (
    <div className="container mt-4">
      <div className="mb-4">
        <h2 className="fw-bold">{id ? "Edit Role" : "New Role"}</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm p-4 mb-4">
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Role Name</label>
              <input
                className="form-control"
                value={formValues.roleName}
                onChange={e => setFormValues({ ...formValues, roleName: e.target.value })}
                required
                placeholder="Role Name"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                value={formValues.description}
                onChange={e => setFormValues({ ...formValues, description: e.target.value })}
                rows={1}
                placeholder="Description"
              />
            </div>
          </div>
        </div>
        {/* Permissions Table */}
        <div className="card shadow-sm p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            {/* Left side */}
            <div>
              <span className="fw-semibold"><h5>Permissions</h5></span>
            </div>

            {/* Right side */}
            <div className="d-flex align-items-center">
              <span className="me-2 fw-semibold">Select All</span>
              <input
                type="checkbox"
                checked={isGlobalAllChecked}
                onChange={e => handleGlobalSelectAll(e.target.checked)}
                style={{ width: 22, height: 22 }}
              />
            </div>
          </div>
          {permissionGroups.map((group) => (
            <div key={group._id} className="mb-4 border rounded-3 p-3 bg-white">
              <div className="fw-bold mb-2"><h5>{group.name}</h5></div>
              <table className="table table-bordered mb-0 rounded-3">
                <thead>
                  <tr>
                    <th
                    style={{
                      backgroundColor: '#D5D8E7',
                      color: '#3F3F46',
                      border: '2px #000',
                      borderRadius:'2px',
                      // padding: '12px 8px',
                      // letterSpacing: '0.5px',
                    }}
                    >Module</th>
                    {(group.permissions && group.permissions.length > 0) &&
                      group.permissions[0].actions.map(act => (
                        <th key={act._id} className="text-center"
                        style={{
                        backgroundColor: '#D5D8E7',
                        color: '#3F3F46',
                        border: '2px #000',
                        // padding: '12px 8px',
                        // letterSpacing: '0.5px',
                    }}
                    >{act.name}</th>
                      ))}
                    <th className="text-center"
                    style={{
                      backgroundColor: '#D5D8E7',
                      color: '#3F3F46',
                      border: '2px #000',
                    }}
                    >Select All</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.permissions || []).map(permission => (
                    <tr key={permission._id}>
                      <td><h6>{permission.name}</h6></td>
                      {(permission.actions || []).map(act => (
                        <td key={act._id} className="text-center">
                          <input
                            type="checkbox"
                            checked={formValues.permissions[group._id]?.[permission._id]?.[act._id] || false}
                            onChange={e =>
                              handlePermissionChange(group._id, permission._id, act._id, e.target.checked)
                            }
                            style={{ width: 22, height: 22 }}
                          />
                        </td>
                      ))}
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={formValues.permissions[group._id]?.[permission._id]?.selectAll || false}
                          onChange={e => handleRowSelectAll(group._id, permission._id, e.target.checked)}
                          style={{ width: 22, height: 22 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary px-4"
            onClick={() => navigate("/admin/userRole")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary px-4">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}